import { z } from 'zod';
import { jsonFormatSchema } from './generation.js';
/**
 * Generation requests and responses for product frontends, sent to the AI API's
 * app route (`POST /v1/app/generate`) with a product-issued token.
 * Zod validates values at runtime; the exported types describe the same contract.
 */
export const providers = [
    'openai',
    'gemini',
    'self_hosted',
];
export const appRoutingReasons = [
    'default', // The site's configured provider and model.
    'explicit', // The caller supplied a provider or model override.
    'cutover', // The schedule redirected a self-hosted request.
    'failover', // An eligible self-hosted failure triggered the fallback provider.
];
/**
 * Client-side validation limits; the AI API enforces the same limits independently.
 * Provider, model and routing defaults belong to the site's entry in the AI API.
 */
export const generationLimits = {
    maxRequestBytes: 262_144,
    maxOutputTokens: 32_768,
};
const nonEmptyTextSchema = z.string().refine(text => text.trim().length > 0, 'Text must not be empty.');
export const appMessageSchema = z.strictObject({
    role: z.enum(['system', 'user', 'assistant']),
    content: nonEmptyTextSchema,
});
/** Options accepted by ai.generate() and this.aiGenerate(). Unknown fields are rejected. */
export const appGenerateRequestSchema = z.strictObject({
    /** Overrides the site default; the AI API rejects providers the site may not use. */
    provider: z.enum(providers).optional(),
    /** Must be the provider's configured model or listed in the site's allowedModels. */
    model: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/).optional(),
    /** A prompt string, or 1–100 messages containing at least one user message. */
    input: z.union([
        nonEmptyTextSchema,
        z.array(appMessageSchema)
            .min(1)
            .max(100)
            .refine(messages => messages.some(message => message.role === 'user'), 'Include a user message.'),
    ]),
    /** Request structured output. The AI API validates result.data against this JSON Schema. */
    format: jsonFormatSchema
        .refine(format => format.schema.type === 'object', 'The schema root must be an object.')
        .optional(),
    /** Optional sampling temperature passed to the selected provider. */
    temperature: z.number().min(0).max(2).optional(),
    /** Optional cap on generated tokens. */
    max_output_tokens: z.number()
        .int()
        .min(1)
        .max(generationLimits.maxOutputTokens)
        .optional(),
});
const tokenUsageSchema = z.object({
    input_tokens: z.number().int().nonnegative(),
    output_tokens: z.number().int().nonnegative(),
    total_tokens: z.number().int().nonnegative(),
});
// Used to ignore unknown response fields so a newer AI API can serve an older frontend.
export const appGenerateResultSchema = z.object({
    /** Generated text, including serialized JSON when structured output was requested. */
    text: z.string(),
    /** Parsed structured output, when requested. Its properties come from the caller's schema. */
    data: z.unknown().optional(),
    provider: z.enum(providers),
    /** Model selected by the application router. */
    model: z.string(),
    /** Model identity reported by the provider. */
    actualModel: z.string(),
    routingReason: z.enum(appRoutingReasons),
    /** The route this one replaced, for `cutover` and `failover`. */
    fallbackFrom: z.object({
        provider: z.enum(providers),
        model: z.string(),
    }).optional(),
    /** Null when the provider did not report usable token counts. */
    usage: tokenUsageSchema.nullable(),
    durationMs: z.number().nonnegative(),
    requestId: z.string().optional(),
});
